import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import adminAPI from "./apiClient";
import type { ConversationDetail as ConversationDetailType, Turn, ConversationSession } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { channelMeta } from "./channelMeta";
import { formatDateWithZone } from "../../utils/formatDate";

interface EnhancedTurn extends Turn {
  action_count: number;
  output_count: number;
}

export default function ConversationDetail(): JSX.Element {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ConversationDetailType | null>(null);
  const [sessions, setSessions] = useState<ConversationSession[]>([]);
  const [turns, setTurns] = useState<EnhancedTurn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProjectDetails();
  }, [conversationId]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await adminAPI.getConversationDetail(conversationId!);
      setDetail(data);
      setSessions(data.conversations || []);

      const enhancedTurns = data.turns.map(turn => {
        const turnActions = data.actions.filter(action => action.turn_id === turn.turn_id);
        const turnOutputs = data.turn_outputs.filter(output => output.turn_id === turn.turn_id);

        return {
          ...turn,
          action_count: turnActions.length,
          output_count: turnOutputs.length
        };
      });

      setTurns(enhancedTurns);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (milliseconds: number): string => {
    if (!milliseconds || milliseconds === 0) return "\u2014";
    if (milliseconds < 1000) return `${milliseconds}ms`;
    if (milliseconds < 60000) return `${(milliseconds / 1000).toFixed(1)}s`;
    return `${(milliseconds / 60000).toFixed(1)}m`;
  };

  const getFeedbackIcon = (thumbsUp: boolean, thumbsDown: boolean): string => {
    if (thumbsUp) return "\uD83D\uDC4D";
    if (thumbsDown) return "\uD83D\uDC4E";
    return "\u2014";
  };

  const getFeedbackColor = (thumbsUp: boolean, thumbsDown: boolean): string => {
    if (thumbsUp) return "text-green-600";
    if (thumbsDown) return "text-red-600";
    return "text-gray-400";
  };

  /** Find which session index a turn belongs to */
  const getSessionIndex = (turn: EnhancedTurn): number => {
    return sessions.findIndex(s => s.conversation_id === turn.conversation_id);
  };

  /** Check if we need a session break before this turn */
  const needsSessionBreak = (turn: EnhancedTurn, index: number): boolean => {
    if (index === 0) return false;
    const prevTurn = turns[index - 1];
    return prevTurn.conversation_id !== turn.conversation_id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Conversation not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Project Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Project Conversation Details</h2>
          {error && (
            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
              API error - {error}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="xl:col-span-2">
            <label className="block text-sm font-medium text-gray-500">Project ID</label>
            <p className="mt-1 text-sm font-mono text-gray-900">{detail.project_id || detail.conversation_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Username</label>
            <p className="mt-1 text-sm text-gray-900">{detail.username}</p>
          </div>
          <div className="xl:col-span-2">
            <label className="block text-sm font-medium text-gray-500">Entra Object ID</label>
            <p className="mt-1 text-sm font-mono text-gray-600">
              {detail.entra_oid || <span className="text-gray-400">Not available</span>}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Environment</label>
            <span className={`mt-1 inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              detail.environment === 'prod' ? 'bg-red-100 text-red-800' :
              detail.environment === 'dev' ? 'bg-blue-100 text-blue-800' :
              detail.environment === 'staging' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {detail.environment}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Source</label>
            {(() => {
              const meta = channelMeta(detail.channel_id);
              return (
                <span className={`mt-1 inline-flex px-2 py-1 text-xs font-medium rounded-full ${meta.cls}`}>
                  {meta.label}
                </span>
              );
            })()}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Duration</label>
            <div className="flex items-center space-x-2 mt-1">
              <p className="text-sm text-gray-900">{formatDuration(detail.total_time_ms)}</p>
              {detail.hit_limits && (
                <span className="text-orange-600" title="Rate limits were hit during this conversation">
                  {"\uD83D\uDEAB"}
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Total Cost</label>
            <div className="mt-1">
              <p className="text-sm font-medium text-gray-900">
                {formatCost(getTotalCostFromApi(
                  detail.total_input_cost_usd,
                  detail.total_output_cost_usd
                ))}
              </p>
              {(detail.total_prompt_tokens || detail.total_completion_tokens) && (
                <p className="text-xs text-gray-500">
                  {((detail.total_prompt_tokens || 0) + (detail.total_completion_tokens || 0)).toLocaleString()} tokens
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Sessions</label>
            <p className="mt-1 text-sm text-gray-900">{sessions.length}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Created</label>
            <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(detail.created_at)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <div className="flex items-center space-x-2 mt-1">
              {detail.hit_limits && (
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                  Rate Limited
                </span>
              )}
              {turns.some(turn => turn.error) && (
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                  Has Errors
                </span>
              )}
              {!detail.hit_limits && !turns.some(turn => turn.error) && (
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  Normal
                </span>
              )}
            </div>
          </div>
        </div>

        {detail.updated_at && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-500">Last Updated</label>
            <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(detail.updated_at)}</p>
          </div>
        )}

        {/* Error Summary */}
        {turns.some(turn => turn.error) && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <span className="text-red-600 mr-2 text-lg">{"\u26A0\uFE0F"}</span>
              <span className="text-sm font-medium text-red-800">
                This project has {turns.filter(turn => turn.error).length} turn(s) with errors
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Turns List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            All Turns ({turns.length}) across {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {turns.map((turn, index) => (
            <React.Fragment key={turn.turn_id}>
              {/* Session Break Marker */}
              {needsSessionBreak(turn, index) && (
                <div className="px-6 py-3 bg-amber-50 border-y-2 border-amber-300">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="h-px flex-1 bg-amber-300"></div>
                    <div className="flex items-center space-x-2 text-amber-700">
                      <span className="text-sm font-semibold">Session Break</span>
                      <span className="text-xs text-amber-600">
                        (Session {getSessionIndex(turn) + 1} of {sessions.length})
                      </span>
                    </div>
                    <div className="h-px flex-1 bg-amber-300"></div>
                  </div>
                  <p className="text-xs text-amber-600 text-center mt-1">
                    New connection started at {formatDateWithZone(sessions[getSessionIndex(turn)]?.created_at || turn.created_at)}
                  </p>
                </div>
              )}

              {/* Session 1 header for very first turn */}
              {index === 0 && sessions.length > 1 && (
                <div className="px-6 py-2 bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center justify-center space-x-2 text-blue-700">
                    <span className="text-xs font-medium">
                      Session 1 of {sessions.length} &mdash; started {formatDateWithZone(sessions[0]?.created_at || turn.created_at)}
                    </span>
                  </div>
                </div>
              )}

              {/* Turn Row */}
              <div
                className="p-6 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/conversation/${turn.conversation_id}/turn/${turn.turn_id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                      <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Turn {turn.turn_id}</p>
                      <p className="text-sm text-gray-500">{formatDateWithZone(turn.created_at)}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {turn.error && (
                      <div className="flex items-center space-x-1 text-red-600">
                        <span className="text-lg">{"\u26A0\uFE0F"}</span>
                        <span className="text-sm font-medium">Error</span>
                      </div>
                    )}
                    {turn.hit_limits && (
                      <div className="flex items-center space-x-1 text-orange-600">
                        <span className="text-lg">{"\uD83D\uDEAB"}</span>
                        <span className="text-sm font-medium">Rate Limited</span>
                      </div>
                    )}
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {turn.action_count || 0} actions &bull; {turn.output_count || 0} outputs
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDuration(turn.total_time_ms)}
                      </div>
                      {(turn.total_input_cost_usd || turn.total_output_cost_usd) && (
                        <div className="text-sm font-medium text-green-700">
                          {formatCost(getTotalCostFromApi(
                            turn.total_input_cost_usd,
                            turn.total_output_cost_usd
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`text-lg ${getFeedbackColor(turn.thumbs_up, turn.thumbs_down)}`}>
                      {getFeedbackIcon(turn.thumbs_up, turn.thumbs_down)}
                    </div>
                  </div>
                </div>

                {/* User Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">User Input</label>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-900">{turn.user_input_text}</p>
                  </div>
                </div>

                {/* Final Response */}
                {turn.final_chat_response && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Final Response</label>
                    <div className="bg-blue-50 p-3 rounded-md">
                      <p className="text-sm text-gray-900">
                        {turn.final_chat_response.length > 200
                          ? `${turn.final_chat_response.substring(0, 200)}...`
                          : turn.final_chat_response
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Feedback */}
                {turn.feedback_comment && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Feedback</label>
                    <div className="bg-yellow-50 p-3 rounded-md">
                      <p className="text-sm text-gray-900">{turn.feedback_comment}</p>
                    </div>
                  </div>
                )}

                {/* Error Status */}
                {turn.error && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Error Status</label>
                    <div className="bg-red-50 p-3 rounded-md border border-red-200">
                      <div className="flex items-center">
                        <span className="text-red-600 mr-2">{"\u26A0\uFE0F"}</span>
                        <span className="text-sm font-medium text-red-800">This turn encountered an error</span>
                      </div>
                      {turn.error_text && (
                        <p className="text-sm text-red-700 mt-1">{turn.error_text}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <a
                    href={`/admin/conversation/${turn.conversation_id}/turn/${turn.turn_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Turn Details &rarr;
                  </a>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {turns.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No turns found for this project.</div>
          </div>
        )}
      </div>
    </div>
  );
}
